import React, {useContext, useReducer} from "react";
import {AppActionTypes, LOGIN, LOGOUT, REGISTER} from "./actions";
import {AppState} from "../models/AppState";
import {CUSTOMER_KEY, LOGIN_URL, LOGOUT_URL, REGISTER_URL} from "../constants/constants";
import reducer from "./reducer";

type AppContextType = {
    state: AppState;
    dispatch: React.Dispatch<AppActionTypes>;
    registerUser: (userInfo: {
        first_name: string;
        last_name: string;
        gender: 'male' | 'female' | 'nonBinary';
        email: string;
        phone: string;
        password: string;
        customer_type: 0 | 1 | 2;
    }) => Promise<{
        success: boolean;
        data?: any;
        error?: string;
    }>;
    loginUser: (email: string, password: string) => Promise<{
        success: boolean;
        data?: any;
        error?: [
            {
                name: string,
                message: string,
            }
        ];
    }>;
    logoutUser: () => Promise<boolean>;
}

const customer = sessionStorage.getItem(CUSTOMER_KEY);
const initialState: AppState = {
    customer: customer ? JSON.parse(customer) : null,
}
type AppProviderProps = {
    children: React.ReactNode;
}
const AppContext = React.createContext<AppContextType | undefined>(undefined)

const AppProvider: React.FC<AppProviderProps> = ({children}) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const loginUser = async (email: string, password: string) => {
        try {
            const response = await fetch(LOGIN_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({email, password}),
                // credentials: 'include'
            });

            const responseData = await response.json();

            if (responseData.success) {
                const customer = responseData.data.customer
                sessionStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer))
                dispatch({type: LOGIN, payload: {customer}})
                return {
                    success: true,
                    data: responseData.data
                };
            } else if (!responseData.success) {
                const errorMessage = responseData.error?.[0]?.message || "An error occurred. Please try again.";
                return {
                    success: false,
                    error: errorMessage || "Unknown error occurred."
                };
            } else {
                return {
                    success: false,
                    error: `Server responded with status: ${response.status}`
                };
            }

        } catch (error) {
            return {
                success: false,
                error: "Unable to connect. Please check your internet connection and try again."
            };
        }
    };
    const registerUser = async (userInfo: {
        first_name: string,
        last_name: string,
        gender: 'male' | 'female' | 'nonBinary',
        email: string,
        phone: string,
        password: string,
        customer_type: 0 | 1 | 2,
    }) => {
        try {
            const response = await fetch(REGISTER_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userInfo),
                credentials: 'include'
            });

            const responseData = await response.json();

            if (responseData.success) {
                const customer = responseData.data.customer
                sessionStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer))
                dispatch({type: REGISTER, payload: {customer}})
                return {
                    success: true,
                    data: responseData.data
                };
            } else if (!responseData.success) {
                const errorMessage = responseData.error?.[0]?.message || "An error occurred. Please try again.";
                return {
                    success: false,
                    error: errorMessage || "Unknown error occurred."
                };
            } else {
                return {
                    success: false,
                    error: `Server responded with status: ${response.status}`
                };
            }

        } catch (error) {
            return {
                success: false,
                error: "Unable to connect. Please check your internet connection and try again."
            };
        }
    };
    const logoutUser = async (): Promise<boolean> => {
        try {
            const response = await fetch(LOGOUT_URL, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status !== 200) {
                const responseData = await response.json();
                console.error('Logout failed:', responseData);
                return false;
            }
            sessionStorage.removeItem(CUSTOMER_KEY)
            dispatch({type: LOGOUT})
            return true;
        } catch (error) {
            console.error('Network error during logout:', error);
            return false;
        }
    };

    return (<AppContext.Provider
            value={{
                state: state,
                dispatch,
                registerUser,
                loginUser,
                logoutUser,
            }}
        >{children}</AppContext.Provider>

    );
}

const useAppContext = () => {
    return useContext(AppContext);
};
export {AppProvider, initialState, useAppContext};